import { CircleInfoIcon } from "@xola/icons";
import { flash, FormGroup, SubmitButton, Switch, Table, Tooltip } from "@xola/ui-kit";
import { FormField, InputField, SecondaryButton, TextareaField } from "common";
import { orderBy } from "lodash";
import React from "react";
import { useMutation } from "react-query";
import { CatalogItemObject, PickupCatalogItem, UnitType } from "types";
import { PickupLocationItem } from "./PickupLocationItem";
import { api } from "@/api/api";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { useInvalidate } from "@/hooks/useInvalidation";
import { getSeller } from "@/utils/getSeller";

interface PickupLocationListProps {
    pickupLocations: PickupCatalogItem[];
}

interface PickupCatalogItemsWithDeclineValues {
    allowDeclineMessage?: boolean;
    pickupLocations: PickupCatalogItem[];
    caption: string;
    name: string;
}

interface PickupCatalogDeclinePayload {
    name: string;
    caption?: string;
    object: CatalogItemObject.PICKUP_CATALOG_ITEM;
    location: null;
    isDeclinePickupItem: boolean;
    arrivalOffset: number | null;
    pricePerPerson?: null;
    pricePerGroup?: null;
    seller: { id: string };
    type: UnitType.PICKUP;
    unitType: UnitType.PICKUP;
    allowDeclineMessage?: boolean;
}

export const PickupLocationList = ({ pickupLocations }: PickupLocationListProps) => {
    const invalidate = useInvalidate();

    const locationWithDecline = pickupLocations.find((location) => location.isDeclinePickupItem);

    const { mutateAsync: upsertGlobalPickupLocation } = useMutation(
        async (payload: PickupCatalogDeclinePayload & { id?: string }) => {
            if (payload.id) {
                return api.put<PickupCatalogItem>(`/catalogItems/${payload.id}`, payload);
            }

            return api.post<PickupCatalogItem>("/catalogItems", payload);
        },
    );

    const { mutateAsync: deletePickupLocation } = useMutation(async ({ id }: { id: ID }) => {
        return api.delete(`/catalogItems/${id}`);
    });

    const handleSubmit = async (values: PickupCatalogItemsWithDeclineValues) => {
        if (values.allowDeclineMessage) {
            const payload: PickupCatalogDeclinePayload & { id?: string } = {
                id: locationWithDecline?.id,
                name: values.name,
                caption: values.caption,
                object: CatalogItemObject.PICKUP_CATALOG_ITEM,
                location: null,
                isDeclinePickupItem: values.allowDeclineMessage,
                arrivalOffset: null,
                pricePerPerson: null,
                pricePerGroup: null,
                seller: { id: getSeller().id },
                type: UnitType.PICKUP,
                unitType: UnitType.PICKUP,
            };

            await upsertGlobalPickupLocation(payload);
        }

        if (!values.allowDeclineMessage && locationWithDecline) {
            await deletePickupLocation({ id: locationWithDecline.id });
        }

        invalidate("catalogItems");

        flash.show({ text: "Pickup Locations updated", color: "success" });
    };

    const filteredPickupLocations = orderBy(
        pickupLocations.filter((location) => location.id !== locationWithDecline?.id),
        ["name"],
        ["asc"],
    );

    const initialValues = {
        allowDeclineMessage: Boolean(locationWithDecline),
        caption: locationWithDecline?.caption ?? "",
        name: locationWithDecline?.name ?? `I don't need to be picked up`,
        pickupLocations: filteredPickupLocations || [],
    };

    return (
        <XolaFormik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
            {({ handleSubmit, setFieldValue, values, handleReset, isSubmitting }) => {
                return (
                    <>
                        <Table>
                            <Table.Head>
                                <Table.Row>
                                    <Table.Header>Name</Table.Header>
                                    <Table.Header>Price Per Guest</Table.Header>
                                    <Table.Header>Price Per Group</Table.Header>
                                    <Table.Header className="flex items-center space-x-1">
                                        <p>Timing</p>
                                        <Tooltip content="The actual pickup time is calculated from the event start time, factoring in event arrival time if configured">
                                            <CircleInfoIcon />
                                        </Tooltip>
                                    </Table.Header>
                                    <Table.Header>Actions</Table.Header>
                                </Table.Row>
                            </Table.Head>

                            <Table.Body>
                                {values.pickupLocations.map((pickupLocation) => {
                                    return (
                                        <PickupLocationItem key={pickupLocation.id} pickupLocation={pickupLocation} />
                                    );
                                })}
                            </Table.Body>
                        </Table>
                        <Switch.Group className="flex items-start justify-start">
                            <Switch
                                isChecked={values.allowDeclineMessage}
                                size="medium"
                                data-testid="allow-decline-pickup-switch"
                                onChange={(value: boolean) => setFieldValue("allowDeclineMessage", value)}
                            />
                            <div className="ml-2">Allow Customers to decline pickup</div>
                        </Switch.Group>
                        {values.allowDeclineMessage && (
                            <FormGroup className="ml-10 max-w-[400px] space-y-4">
                                <FormField name="name" label="Pickup location dropdown option">
                                    <InputField />
                                </FormField>

                                <FormField name="caption" label="Custom message to include in emails">
                                    <TextareaField rows={2} />
                                </FormField>
                            </FormGroup>
                        )}

                        <ContentLayout.Footer preferenceSidebar>
                            <SubmitButton
                                isLoading={isSubmitting}
                                data-testid="pickup-location-list-save-button"
                                onClick={handleSubmit}
                            >
                                Save
                            </SubmitButton>
                            <SecondaryButton
                                type="reset"
                                data-testid="pickup-location-list-cancel-button"
                                onClick={handleReset}
                            >
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>
                    </>
                );
            }}
        </XolaFormik>
    );
};
